import { forwardRef, useId, type HTMLAttributes, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { sizeClass, type DsSize } from '@/ds/tone'
import { DsIconGlyph } from '@/ds/icon'
import { DsIcons } from '@/ds/icons'

export interface DsFieldShellProps {
  label?: string
  required?: boolean
  optionalLabel?: string
  helper?: string
  error?: string | null
  counter?: string
  disabled?: boolean
  /** id of the control the label points at; generated when omitted (read it back from the render prop). */
  htmlFor?: string
  children: ReactNode | ((ids: { id: string; describedBy?: string }) => ReactNode)
  className?: string
}

export function DsFieldShell({ label, required, optionalLabel, helper, error, counter, disabled, htmlFor, children, className }: DsFieldShellProps) {
  const generated = useId()
  const id = htmlFor ?? generated
  const helperId = `${id}-helper`
  const describedBy = error || helper ? helperId : undefined
  return (
    <div className={cn('flex flex-col', disabled && 'opacity-60', className)}>
      {label && (
        <label id={`${id}-label`} htmlFor={id} className="mb-1.5 text-small-strong text-foreground">
          {label}
          {required && <span className="text-danger-soft-foreground"> *</span>}
          {!required && optionalLabel && <span className="ml-1 font-normal text-muted-foreground">({optionalLabel})</span>}
        </label>
      )}
      {typeof children === 'function' ? children({ id, describedBy }) : children}
      {(error || helper || counter) && (
        <div className="mt-1.5 flex items-start justify-between gap-2">
          {error ? (
            <p id={helperId} role="alert" className="inline-flex items-start gap-1 text-caption text-danger-soft-foreground">
              <DsIconGlyph icon={DsIcons.errorOutline} size={14} className="mt-px" />
              {error}
            </p>
          ) : helper ? (
            <p id={helperId} className="text-caption text-muted-foreground">
              {helper}
            </p>
          ) : (
            <span />
          )}
          {counter && <span className="shrink-0 text-caption tabular-nums text-muted-foreground">{counter}</span>}
        </div>
      )}
    </div>
  )
}

export interface DsFieldFrameProps extends HTMLAttributes<HTMLDivElement> {
  focused?: boolean
  hasError?: boolean
  filled?: boolean
  size?: DsSize
  multiline?: boolean
  disabled?: boolean
  interactive?: boolean
}

export const DsFieldFrame = forwardRef<HTMLDivElement, DsFieldFrameProps>(function DsFieldFrame(
  { focused, hasError, filled, size = 'md', multiline, disabled, interactive, className, children, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        'flex w-full items-center gap-2 rounded-lg border bg-card px-3.5 text-body text-foreground transition-[border-color,box-shadow] motion-fast',
        multiline ? 'min-h-control-md items-start py-3' : sizeClass[size].height,
        filled && 'bg-muted',
        hasError ? 'border-danger' : focused ? 'border-ring' : 'border-input',
        focused && (hasError ? 'ring-focus-danger' : 'ring-focus-soft'),
        interactive && !disabled && 'cursor-pointer hover:border-ring/60',
        disabled && 'cursor-not-allowed',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  )
})
